import React, { useEffect, useState } from "react";
import axios from "axios";
import { FaCheck, FaClock, FaGift } from "react-icons/fa";
import { getProductImageUrl, handleProductImageError } from "../../util/storeLogoHelper";

const money = (symbol, value) => `${symbol} ${Math.round(Number(value || 0)).toLocaleString()}`;
// Above this many sizes or colors a dropdown reads better than a wall of chips.
const MAX_CHIPS = 8;
const clock = (seconds) => `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, "0")}`;

/**
 * One-click add-on on the order confirmation page. The server decides whether
 * the order can still change and what it costs; this only shows the offer.
 */
export default function PostPurchaseOffer({ orderId, trackingNo, currencySymbol = "Rs.", onAdded }) {
  const [offer, setOffer] = useState(null);
  const [secondsLeft, setSecondsLeft] = useState(0);
  const [busyId, setBusyId] = useState(null);
  const [added, setAdded] = useState(null);
  const [error, setError] = useState("");
  const [choices, setChoices] = useState({});

  useEffect(() => {
    if (!orderId || !trackingNo) return undefined;
    let active = true;
    axios.get(`/api/orders/${orderId}/offer?tracking_no=${encodeURIComponent(trackingNo)}`)
      .then((res) => {
        if (!active || !res.data.eligible || !res.data.offers?.length) return;
        setOffer(res.data);
        setSecondsLeft(Math.floor(res.data.expires_in || 0));
      })
      .catch(() => {});
    return () => {
      active = false;
    };
  }, [orderId, trackingNo]);

  useEffect(() => {
    if (!offer || added) return undefined;
    const timer = setInterval(() => setSecondsLeft((left) => Math.max(0, left - 1)), 1000);
    return () => clearInterval(timer);
  }, [offer, added]);

  if (!offer) return null;
  if (!added && secondsLeft <= 0) return null;

  const add = (card) => {
    const variantId = choices[card.id] || null;
    if (card.variants?.length && !variantId) {
      setError(`Choose a size or color for ${card.name} first.`);
      return;
    }
    setBusyId(card.id);
    setError("");
    axios.post(`/api/orders/${orderId}/add-offer`, { tracking_no: trackingNo, product_id: card.id, variant_id: variantId })
      .then((res) => {
        if (res.data.status === 200) {
          setAdded({ ...res.data.item, image: card.image });
          if (onAdded) onAdded(res.data.item, res.data.grand_total);
        } else {
          setError(res.data.message || "Could not add this item.");
        }
      })
      .catch(() => setError("Could not add this item. Please try again."))
      .finally(() => setBusyId(null));
  };

  if (added) {
    return (
      <div className="pp-offer is-done mb-4" role="status">
        <span className="pp-offer-done-icon"><FaCheck size={14} /></span>
        <div>
          <strong>{added.name} was added to your order.</strong>
          <div className="text-muted small">It ships with your order. Pay {money(currencySymbol, added.price)} more on delivery.</div>
        </div>
      </div>
    );
  }

  return (
    <section className="pp-offer mb-4" aria-label={offer.title}>
      <div className="pp-offer-head">
        <div>
          <span className="pp-offer-tag"><FaGift size={12} /> {offer.discount > 0 ? `${offer.discount}% off, only now` : "Only now"}</span>
          <h5 className="fw-bold mb-0 mt-2">{offer.title}</h5>
          <p className="text-muted small mb-0">Added to the same delivery. No need to check out again, pay on delivery.</p>
        </div>
        <span className="pp-offer-timer" aria-label="Time left"><FaClock size={12} /> {clock(secondsLeft)}</span>
      </div>
      <div className="pp-offer-list">
        {offer.offers.map((card) => {
          const options = card.variants || [];
          const chosen = options.find((option) => option.id === choices[card.id]);
          const price = chosen ? chosen.final_price : card.price;
          const offerPrice = chosen ? chosen.offer_price : card.offer_price;
          const isFrom = options.length > 1 && !chosen && new Set(options.map((option) => option.offer_price)).size > 1;
          return (
            <div className="pp-offer-card" key={card.id}>
              <img src={getProductImageUrl(card.image)} alt={card.name} loading="lazy" onError={handleProductImageError} />
              <div className="pp-offer-info">
                <span className="pp-offer-name" title={card.name}>{card.name}</span>
                <span>
                  {offerPrice < price && <s className="text-muted me-2">{money(currencySymbol, price)}</s>}
                  {isFrom && <span className="text-muted small me-1">from</span>}
                  <strong>{money(currencySymbol, offerPrice)}</strong>
                </span>
                {options.length > MAX_CHIPS && (
                  <select
                    className="form-select form-select-sm pp-offer-select"
                    value={choices[card.id] || ""}
                    aria-label={`Size or color for ${card.name}`}
                    onChange={(event) => {
                      setError("");
                      setChoices((current) => ({ ...current, [card.id]: event.target.value ? Number(event.target.value) : null }));
                    }}
                  >
                    <option value="">Choose size or color</option>
                    {options.map((option) => <option key={option.id} value={option.id}>{option.title}</option>)}
                  </select>
                )}
                {options.length > 0 && options.length <= MAX_CHIPS && (
                  <div className="pp-offer-chips" role="group" aria-label={`Size or color for ${card.name}`}>
                    {options.map((option) => (
                      <button
                        type="button"
                        key={option.id}
                        className={`pp-offer-chip ${option.id === choices[card.id] ? "is-active" : ""}`}
                        aria-pressed={option.id === choices[card.id]}
                        onClick={() => {
                          setError("");
                          setChoices((current) => ({ ...current, [card.id]: current[card.id] === option.id ? null : option.id }));
                        }}
                      >
                        {option.title}
                      </button>
                    ))}
                  </div>
                )}
              </div>
              <button type="button" className="pp-offer-btn" disabled={busyId !== null} onClick={() => add(card)}>
                {busyId === card.id ? "Adding…" : "Add to order"}
              </button>
            </div>
          );
        })}
      </div>
      {error && <div className="text-danger small mt-2">{error}</div>}
    </section>
  );
}
